import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { UpsertClaimMonetaryDemandInput } from "@upclaim/sdk/src/data-contracts";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { MonetaryDemandType } from "../lib/monetary-demand-types";

function useInvalidateClaim(claimId: string) {
  const queryClient = useQueryClient();
  return async (success: boolean) => {
    if (success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    }
  };
}

/** Mirrors UpsertClaimMonetaryDemand.upsertAdditionalRequest (Vue) — PUT /admin/claims/{id}/monetary-demands. */
export function useUpsertMonetaryDemand(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: (payload: UpsertClaimMonetaryDemandInput) =>
      notify.promise(adminAPI.upsertClaimMonetaryDemand(claimId, payload), {
        loading: "Enregistrement de la demande…",
        success: "Demande enregistrée avec succès",
      }),
    onSuccess: ({ success }) => invalidate(success),
  });
}

/** Mirrors MonetaryDemands.deleteClaimMonetaryDemand (Vue) — DELETE /admin/claims/{id}/monetary-demands/{type}. */
export function useDeleteMonetaryDemand(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: (monetaryDemandType: MonetaryDemandType) =>
      notify.promise(adminAPI.deleteClaimMonetaryDemand(claimId, monetaryDemandType), {
        loading: "Suppression de la demande…",
        success: "Demande supprimée avec succès",
      }),
    onSuccess: ({ success }) => invalidate(success),
  });
}
